/**
 * SRV-F2-2: Supplier Settlement Page
 * Admin → Gyártói garanciás elszámolás oldal.
 * Jogosult munkalapok listázása, gyűjtőszámla generálás.
 */

import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface EligibleWorksheet {
  id: string;
  worksheetNumber: string;
  partnerName: string;
  brand: string;
  completedAt: string;
  laborCost: number;
  partsCost: number;
  totalAmount: number;
}

interface BatchInvoiceResult {
  invoiceId: string;
  invoiceNumber: string;
  totalNet: number;
  worksheetCount: number;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);

export function SupplierSettlementPage() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const navigate = useNavigate();

  const [supplierId, setSupplierId] = useState('');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [eligible, setEligible] = useState<EligibleWorksheet[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<BatchInvoiceResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadEligible = useCallback(async () => {
    if (!supplierId || !periodStart || !periodEnd) {
      setError(t('worksheet.supplierSettlement.filterRequired'));
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get<{ data: EligibleWorksheet[] }>(
        `/service/warranty/batch-invoice/eligible?supplierId=${supplierId}&periodStart=${periodStart}&periodEnd=${periodEnd}`
      );
      setEligible(res.data);
    } catch (err) {
      // 49Y hibakód-batch: code-alapú lokalizált hiba (WARRANTY_ELIGIBLE_LIST_FAILED — #897 BE);
      // fallback a régi common.error-ra. Kiváltja a nyers err.message-et.
      setError(getErrorMessage(err, t('common.error')));
    } finally {
      setLoading(false);
    }
  }, [supplierId, periodStart, periodEnd, t, getErrorMessage]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await api.post<{ data: BatchInvoiceResult }>('/service/warranty/batch-invoice', {
        supplierId,
        periodStart,
        periodEnd,
      });
      setResult(res.data);
      setEligible([]);
    } catch (err) {
      // 49Y: code-alapú lokalizált hiba (WARRANTY_BATCH_* / WARRANTY_ALREADY_INVOICED /
      // WORKSHEET_ID_REQUIRED — #897 BE); fallback common.error.
      setError(getErrorMessage(err, t('common.error')));
    } finally {
      setGenerating(false);
    }
  };

  const totalLabor = eligible.reduce((s, w) => s + w.laborCost, 0);
  const totalParts = eligible.reduce((s, w) => s + w.partsCost, 0);
  const totalAmount = eligible.reduce((s, w) => s + w.totalAmount, 0);

  return (
    <div className="min-h-screen kgc-bg">
      <div className="bg-white dark:bg-[#1a4f5c] border-b border-gray-200 dark:border-[#3a9aa8] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/settings')}>
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {t('worksheet.supplierSettlement.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('worksheet.supplierSettlement.subtitle')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6 space-y-6">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-300 dark:border-red-600 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {result && (
          <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-400 dark:border-green-600">
            <h3 className="font-semibold text-green-800 dark:text-green-300">
              {t('worksheet.supplierSettlement.generated')}
            </h3>
            <p className="text-sm text-green-700 dark:text-green-400 mt-1">
              {t('worksheet.supplierSettlement.invoiceNumber')}: {result.invoiceNumber} |{' '}
              {result.worksheetCount} {t('worksheet.supplierSettlement.worksheets')} |{' '}
              {formatCurrency(result.totalNet)}
            </p>
          </div>
        )}

        {/* Szűrők */}
        <Card>
          <CardHeader>
            <CardTitle>{t('worksheet.supplierSettlement.filters')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('worksheet.supplierSettlement.supplier')}
                </label>
                <Input
                  placeholder="Supplier ID (UUID)"
                  value={supplierId}
                  onChange={e => setSupplierId(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('worksheet.supplierSettlement.from')}
                </label>
                <Input
                  type="date"
                  value={periodStart}
                  onChange={e => setPeriodStart(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  {t('worksheet.supplierSettlement.to')}
                </label>
                <Input type="date" value={periodEnd} onChange={e => setPeriodEnd(e.target.value)} />
              </div>
              <div className="flex items-end">
                <Button onClick={loadEligible} disabled={loading}>
                  {loading ? '...' : t('worksheet.supplierSettlement.search')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Jogosult munkalapok */}
        {eligible.length > 0 && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {t('worksheet.supplierSettlement.eligible')} ({eligible.length})
                </CardTitle>
                <Button onClick={handleGenerate} disabled={generating}>
                  {generating ? '...' : t('worksheet.supplierSettlement.generateInvoice')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-2 px-3">
                        {t('worksheet.supplierSettlement.wsNumber')}
                      </th>
                      <th className="text-left py-2 px-3">
                        {t('worksheet.supplierSettlement.brand')}
                      </th>
                      <th className="text-left py-2 px-3">
                        {t('worksheet.supplierSettlement.completed')}
                      </th>
                      <th className="text-right py-2 px-3">{t('service.laborCost')}</th>
                      <th className="text-right py-2 px-3">{t('service.parts')}</th>
                      <th className="text-right py-2 px-3">{t('common.total')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligible.map(ws => (
                      <tr key={ws.id} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 px-3 font-mono text-xs">{ws.worksheetNumber}</td>
                        <td className="py-2 px-3">{ws.brand}</td>
                        <td className="py-2 px-3">
                          {new Date(ws.completedAt).toLocaleDateString('hu-HU')}
                        </td>
                        <td className="py-2 px-3 text-right">{formatCurrency(ws.laborCost)}</td>
                        <td className="py-2 px-3 text-right">{formatCurrency(ws.partsCost)}</td>
                        <td className="py-2 px-3 text-right font-medium">
                          {formatCurrency(ws.totalAmount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-300 dark:border-gray-600 font-semibold">
                      <td colSpan={3} className="py-2 px-3">
                        {t('common.total')}
                      </td>
                      <td className="py-2 px-3 text-right">{formatCurrency(totalLabor)}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(totalParts)}</td>
                      <td className="py-2 px-3 text-right">{formatCurrency(totalAmount)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
